import { FaBug, FaExclamationTriangle, FaHeart, FaShieldAlt, FaUserInjured } from "react-icons/fa";
import { MdLocalHospital, MdWarning } from "react-icons/md";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table";

type AttendanceCondition = {
	key: string;
	total: number;
};

type Props = {
	aggregatedByAttendanceConditions: AttendanceCondition[];
};

// Função para determinar o ícone baseado na condição de atendimento
const getConditionIcon = (conditionName: string) => {
	const name = conditionName.toLowerCase();

	if (name.includes("animal") || name.includes("peçonhento")) {
		return <FaBug className="text-danger" />;
	}
	if (name.includes("trabalho") && name.includes("biológico")) {
		return <FaExclamationTriangle className="text-warning" />;
	}
	if (name.includes("dengue")) {
		return <FaBug className="text-warning" />;
	}
	if (name.includes("violência") || name.includes("autoprovocada")) {
		return <FaHeart className="text-danger" />;
	}
	if (name.includes("trabalho") && name.includes("grave")) {
		return <FaUserInjured className="text-danger" />;
	}
	if (name.includes("acidente")) {
		return <MdLocalHospital className="text-info" />;
	}
	if (name.includes("emergência") || name.includes("urgente")) {
		return <MdWarning className="text-danger" />;
	}

	return <FaShieldAlt className="text-muted" />;
};

// Função para determinar a cor do badge baseada na condição
const getConditionColor = (conditionName: string) => {
	const name = conditionName.toLowerCase();

	if (name.includes("animal") || name.includes("peçonhento")) {
		return "danger";
	}
	if (name.includes("trabalho") && name.includes("biológico")) {
		return "warning";
	}
	if (name.includes("dengue")) {
		return "warning";
	}
	if (name.includes("violência") || name.includes("autoprovocada")) {
		return "danger";
	}
	if (name.includes("trabalho") && name.includes("grave")) {
		return "danger";
	}
	if (name.includes("acidente")) {
		return "info";
	}
	if (name.includes("emergência") || name.includes("urgente")) {
		return "danger";
	}

	return "secondary";
};

export function AttendanceConditions({ aggregatedByAttendanceConditions }: Props) {
	// Calcular total geral
	const totalConditions = aggregatedByAttendanceConditions.reduce(
		(sum, item) => sum + item.total,
		0
	);

	// Ordenar por quantidade (maior para menor)
	const sortedConditions = [...aggregatedByAttendanceConditions].sort(
		(a, b) => b.total - a.total
	);

	return (
		<Card className="border-0 shadow-sm">
			<Card.Header className="bg-white border-bottom py-3">
				<div className="d-flex align-items-center">
					<FaShieldAlt className="text-muted me-3" size={20} />
					<h6 className="mb-0 text-dark">Condições de Atendimento</h6>
				</div>
			</Card.Header>

			<Card.Body className="p-0">
				<div className="overflow-y-scroll" style={{ maxHeight: "500px" }}>
					<Table responsive hover className="mb-0">
						<thead className="bg-light">
							<tr>
								<th className="ps-3 py-3 border-0">Condição</th>
								<th className="text-center py-3 border-0">Quantidade</th>
								<th className="text-end pe-3 py-3 border-0">Percentual</th>
							</tr>
						</thead>
						<tbody>
							{sortedConditions.map((condition, index) => {
								const percentage = (
									(condition.total / totalConditions) *
									100
								).toFixed(1);
								const badgeColor = getConditionColor(condition.key);

								return (
									<tr key={index} className="border-0">
										<td className="ps-3 py-3">
											<div className="d-flex align-items-center">
												<div className="me-3">
													{getConditionIcon(condition.key)}
												</div>
												<div>
													<div
														className="fw-medium text-dark"
														style={{ fontSize: "0.9rem" }}
													>
														{condition.key}
													</div>
													<Badge bg={badgeColor} className="mt-1">
														{condition.total} casos
													</Badge>
												</div>
											</div>
										</td>
										<td className="text-center py-3">
											<div className="fw-bold text-dark">
												{condition.total.toLocaleString()}
											</div>
										</td>
										<td className="text-end pe-3 py-3">
											<Badge bg="light" text="dark" className="px-3 py-2">
												{percentage}%
											</Badge>
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			</Card.Body>

			<Card.Footer className="bg-light border-top py-3">
				<div className="d-flex justify-content-between align-items-center">
					<div className="text-muted">
						<small>Total de condições registradas</small>
					</div>
					<div className="fw-bold text-dark">{totalConditions.toLocaleString()}</div>
				</div>
			</Card.Footer>
		</Card>
	);
}
